import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = '/api/master-data';

/* ── Inline SVG Icon ── */
const Icon = ({ d, size = 16, strokeWidth = 1.75 }) => (
    <svg
        width={size} height={size} viewBox="0 0 24 24"
        fill="none" stroke="currentColor"
        strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
    >
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const icons = {
    settings: ['M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z', 'M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z'],
    search: ['M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z', 'M21 21l-4.35-4.35'],
    status: ['M22 11.08V12a10 10 0 1 1-5.93-9.14', 'M22 4L12 14.01l-3-3'],
    plus: ['M12 5v14', 'M5 12h14'],
    edit: ['M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7', 'M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z'],
    trash: ['M3 6h18', 'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2'],
    x: ['M18 6 6 18', 'M6 6l12 12'],
    alertTriangle: ['M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z', 'M12 9v4', 'M12 17h.01'],
    chevronDown: 'M6 9l6 6 6-6',
    leave: ['M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2', 'M9 7a4 4 0 1 0 0-8 4 4 0 0 0 0 8z', 'M19 16l2 2 4-4'],
    request: ['M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z'],
    condition: ['M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z'],
};

const COLOR_PALETTE = [
    '#4F6EF7', '#22C55E', '#EF4444', '#F59E0B', '#A855F7',
    '#EC4899', '#6366F1', '#14B8A6', '#F97316', '#6B7280',
    '#1E293B', '#06B6D4', '#84CC16', '#DC2626', '#9333EA',
    '#3B82F6',
];

const TABS = [
    { key: 'general-status', label: 'General Status', iconKey: 'status', category: 'general-status', description: 'Configure system-wide status preferences.' },
    { key: 'leave-requests', label: 'Leave Requests', iconKey: 'leave', category: 'leave-request-type', description: 'Configure types of leave requests available.' },
    { key: 'other-requests', label: 'Other Requests', iconKey: 'request', category: 'other-request-type', description: 'Define miscellaneous request types.' },
    { key: 'item-conditions', label: 'Item Conditions', iconKey: 'condition', category: 'item-conditions', description: 'Set standard condition states for equipment.' },
];

function ItemModal({ tab, initial, onClose, onSave }) {
    const isStatus = tab.key === 'general-status';
    const [form, setForm] = useState(
        initial || { name: '', description: '', color: isStatus ? '#4F6EF7' : null, active: true }
    );
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        if (!form.name.trim()) return;
        setSaving(true);
        try {
            await onSave(form);
        } finally {
            setSaving(false);
        }
    };

    const singular = tab.label.endsWith('s') ? tab.label.slice(0, -1) : tab.label;

    return (
        <div className="md-modal-overlay" onClick={onClose}>
            <div className="md-modal" onClick={e => e.stopPropagation()}>
                <div className="md-modal-header">
                    <h3 className="md-modal-title">{initial?._id ? `Edit ${singular}` : `Add ${singular}`}</h3>
                    <button className="md-modal-close" onClick={onClose}>
                        <Icon d={icons.x} size={18} />
                    </button>
                </div>
                <div className="md-modal-body">
                    <div className="md-form-group">
                        <label className="md-label">Name<span className="md-required">*</span></label>
                        <input
                            className="md-input"
                            value={form.name}
                            onChange={e => setForm({ ...form, name: e.target.value })}
                            placeholder={`Enter ${singular.toLowerCase()} name...`}
                        />
                    </div>
                    <div className="md-form-group">
                        <label className="md-label">Description</label>
                        <textarea
                            className="md-textarea"
                            rows={3}
                            value={form.description}
                            onChange={e => setForm({ ...form, description: e.target.value })}
                            placeholder="Brief description..."
                        />
                    </div>
                    {isStatus && (
                        <div className="md-form-group">
                            <label className="md-label">Color</label>
                            <div className="md-color-palette">
                                {COLOR_PALETTE.map(c => (
                                    <button
                                        key={c}
                                        className={`md-color-swatch ${form.color === c ? 'md-color-active' : ''}`}
                                        style={{ background: c }}
                                        onClick={() => setForm({ ...form, color: c })}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                    <div className="md-form-toggle-row">
                        <label className="md-toggle-label">Active</label>
                        <button
                            className={`md-toggle ${form.active ? 'md-toggle-on' : ''}`}
                            onClick={() => setForm({ ...form, active: !form.active })}
                        >
                            <span className="md-toggle-thumb" />
                        </button>
                    </div>
                </div>
                <div className="md-modal-footer">
                    <button className="md-cancel-btn" onClick={onClose}>Cancel</button>
                    <button className="md-save-btn" onClick={handleSave} disabled={saving || !form.name.trim()} style={{ background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)', borderRadius: 10 }}>
                        {saving ? 'Saving...' : (initial?._id ? 'Update' : 'Create')}
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ── Delete Confirmation ── */
function DeleteModal({ item, label, onClose, onConfirm }) {
    const [deleting, setDeleting] = useState(false);
    return (
        <div className="md-modal-overlay" onClick={onClose}>
            <div className="md-modal md-modal-sm" style={{ width: 400 }} onClick={e => e.stopPropagation()}>
                <div className="md-modal-header">
                    <h3 className="md-modal-title" style={{ color: '#dc2626' }}>
                        <Icon d={icons.alertTriangle} size={18} /> Delete {label}
                    </h3>
                    <button className="md-modal-close" onClick={onClose}>
                        <Icon d={icons.x} size={18} />
                    </button>
                </div>
                <div className="md-modal-body">
                    <p style={{ color: '#475569', lineHeight: 1.6, margin: 0 }}>
                        Are you sure you want to delete <strong>"{item?.name}"</strong>? This action cannot be undone.
                    </p>
                </div>
                <div className="md-modal-footer">
                    <button className="md-cancel-btn" onClick={onClose}>Cancel</button>
                    <button
                        className="md-save-btn"
                        style={{ background: '#dc2626' }}
                        onClick={async () => {
                            setDeleting(true);
                            await onConfirm();
                            setDeleting(false);
                        }}
                        disabled={deleting}
                    >
                        {deleting ? 'Deleting...' : 'Delete'}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function GeneralSettings() {
    const [activeTab, setActiveTab] = useState('general-status');
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [modal, setModal] = useState(null); // null | 'add' | item
    const [deleteModal, setDeleteModal] = useState(null);

    useEffect(() => {
        fetchItems();
    }, [activeTab]);

    const fetchItems = async () => {
        setLoading(true);
        const tab = TABS.find(t => t.key === activeTab);
        try {
            const res = await axios.get(API);
            setItems(res.data.filter(d => d.category === tab.category));
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    };

    const handleSave = async (form) => {
        const tab = TABS.find(t => t.key === activeTab);
        const payload = { ...form, category: tab.category };
        if (form._id) {
            await axios.put(`${API}/${form._id}`, payload);
        } else {
            await axios.post(API, payload);
        }
        setModal(null);
        fetchItems();
    };

    const handleDelete = async () => {
        if (deleteModal?._id) {
            await axios.delete(`${API}/${deleteModal._id}`);
        }
        setDeleteModal(null);
        fetchItems();
    };

    const filtered = items.filter(i =>
        i.name.toLowerCase().includes(search.toLowerCase()) ||
        (i.description || '').toLowerCase().includes(search.toLowerCase())
    );

    const currentTabObj = TABS.find(t => t.key === activeTab);

    return (
        <div className="ts-container">
            {/* Sidebar like Ticket Settings */}
            <aside className="ts-sidebar">
                <div className="ts-nav-item active" style={{ marginBottom: 24, cursor: 'default', background: 'none', color: '#1e293b', paddingLeft: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', opacity: 0.5 }}>Config Categories</span>
                </div>
                {TABS.map(tab => (
                    <div
                        key={tab.key}
                        className={`ts-nav-item ${activeTab === tab.key ? 'active' : ''}`}
                        onClick={() => setActiveTab(tab.key)}
                    >
                        <Icon d={icons[tab.iconKey]} size={18} />
                        <span>{tab.label}</span>
                    </div>
                ))}
            </aside>

            {/* Main Content Area */}
            <main className="ts-main">
                <header className="ts-header">
                    <div className="ts-title-group">
                        <h1>{currentTabObj.label}</h1>
                        <p>{currentTabObj.description}</p>
                    </div>
                    <div className="ts-controls">
                        <div className="md-search-wrap" style={{ margin: 0 }}>
                            <Icon d={icons.search} size={14} />
                            <input className="md-search" placeholder="Quick search..." value={search} onChange={e => setSearch(e.target.value)} />
                        </div>
                        <button className="md-add-btn" onClick={() => setModal('add')} style={{ background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)' }}>
                            <Icon d={icons.plus} size={14} /> Add {currentTabObj.label.endsWith('s') ? currentTabObj.label.slice(0, -1) : currentTabObj.label}
                        </button>
                    </div>
                </header>

                <div className="md-panel" style={{ background: 'white', borderRadius: 16, border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
                    <div className="md-table-wrap">
                        <table className="md-table">
                            <thead>
                                <tr>
                                    <th className="md-th">Name</th>
                                    <th className="md-th">Description</th>
                                    <th className="md-th">Status</th>
                                    {activeTab === 'general-status' && <th className="md-th">Theme</th>}
                                    <th className="md-th" style={{ textAlign: 'right' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={activeTab === 'general-status' ? 5 : 4} className="md-empty">
                                            <div style={{ padding: '60px 0', textAlign: 'center', color: '#94a3b8' }}>Loading...</div>
                                        </td>
                                    </tr>
                                ) : filtered.length === 0 ? (
                                    <tr>
                                        <td colSpan={activeTab === 'general-status' ? 5 : 4} className="md-empty">
                                            <div className="md-empty-inner">
                                                <div className="md-empty-icon">
                                                    <Icon d={icons[currentTabObj.iconKey]} size={32} />
                                                </div>
                                                <p className="md-empty-title">No {currentTabObj.label} Found</p>
                                                <p className="md-empty-sub">Add your first item to get started.</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    filtered.map(item => (
                                        <tr key={item._id} className="md-tr">
                                            <td className="md-td">
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                    {item.color && (
                                                        <span className="md-color-dot" style={{ background: item.color, width: 10, height: 10, borderRadius: '50%', flexShrink: 0 }} />
                                                    )}
                                                    <strong style={{ color: '#1e293b' }}>{item.name}</strong>
                                                </div>
                                            </td>
                                            <td className="md-td" style={{ color: '#64748b', fontSize: 13, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {item.description || 'No operational description provided.'}
                                            </td>
                                            <td className="md-td">
                                                <span className={`md-status-badge ${item.active ? 'md-badge-active' : 'md-badge-inactive'}`}>
                                                    {item.active ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            {activeTab === 'general-status' && (
                                                <td className="md-td">
                                                    {item.color ? (
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                            <div style={{ width: 14, height: 14, borderRadius: 3, background: item.color }} />
                                                            <span style={{ fontSize: 11, fontFamily: 'monospace', color: '#94a3b8' }}>{item.color.toUpperCase()}</span>
                                                        </div>
                                                    ) : '—'}
                                                </td>
                                            )}
                                            <td className="md-td md-actions" style={{ textAlign: 'right' }}>
                                                <button className="md-icon-btn" title="Edit" onClick={() => setModal(item)}>
                                                    <Icon d={icons.edit} size={14} />
                                                </button>
                                                <button className="md-icon-btn md-icon-btn-danger" title="Delete" onClick={() => setDeleteModal(item)}>
                                                    <Icon d={icons.trash} size={14} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </main>

            {/* Modals */}
            {modal && (
                <ItemModal
                    tab={currentTabObj}
                    initial={modal === 'add' ? null : modal}
                    onClose={() => setModal(null)}
                    onSave={handleSave}
                />
            )}
            {deleteModal && (
                <DeleteModal
                    item={deleteModal}
                    label={currentTabObj.label.endsWith('s') ? currentTabObj.label.slice(0, -1) : currentTabObj.label}
                    onClose={() => setDeleteModal(null)}
                    onConfirm={handleDelete}
                />
            )}
        </div>
    );
}

